import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignUpForm } from '@/components/elements/SignUpForm';
import { SignUpVerificationCodeForm } from '@/components/elements/SignUpVerificationCodeForm';
import { INITIAL_LOGIN_PATTERN } from '@/constants/auth';
import { ROUTE_PATH } from '@/constants/routes';
import { MEMBERSHIP_TYPES } from '@/constants/users';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useSuiteLoungeFlow } from '@/hooks/useSuiteLoungeFlow';

interface SignUpFormData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

export const SignUp = () => {
  const navigate = useNavigate();
  const { translateApiError } = useApiErrorTranslation();
  const { isSuiteLoungeFlow, withEntry } = useSuiteLoungeFlow();
  const [step, setStep] = useState<'input' | 'verification'>('input');
  const [formData, setFormData] = useState<SignUpFormData | null>(null);
  const [signupSessionId, setSignupSessionId] = useState<string>('');
  const [error, setError] = useState<{
    message: string;
    detailMessages?: string[];
  } | null>(null);

  const { isRequesting: isCreatingAccount, execute: executeCreateAccount } =
    useApiRequest('POST', 'UserRegistration');

  const handleFormSubmit = async (data: SignUpFormData) => {
    setError(null);

    const result = await executeCreateAccount({
      family_name_jp: data.lastName,
      first_name_jp: data.firstName,
      email: data.email,
      membership_type: isSuiteLoungeFlow
        ? MEMBERSHIP_TYPES.SUITE_LOUNGE
        : MEMBERSHIP_TYPES.ANA_LOUNGE,
    });

    if (result.ok) {
      const responseData = result.data.data;

      // アカウント作成リクエストにて、workhubにすでにアカウントがある場合は、初回ログイン時の分岐処理
      if ('initial_login_pattern' in responseData) {
        const { initial_login_pattern } = responseData;

        // initial_login_pattern に応じて遷移先を変更
        if (
          initial_login_pattern === INITIAL_LOGIN_PATTERN.WORKHUB_NO_CUSTOMER
        ) {
          // WorkhubのANA組織内に会員情報がない場合は氏名が必要なため、氏名入力画面へ
          navigate(withEntry(ROUTE_PATH.HAS_BITKEY_ACCOUNT), {
            state: { email: data.email, initial_login_pattern },
          });
        } else if (
          initial_login_pattern ===
          INITIAL_LOGIN_PATTERN.WORKHUB_EXISTING_CUSTOMER
        ) {
          // WorkhubのANA組織内に会員情報がある場合はリニューアル告知画面へ
          navigate(ROUTE_PATH.RENEWAL_NOTICE, {
            state: { email: data.email, initial_login_pattern },
          });
        }
        return;
      }

      // workhubにアカウントがない場合は、認証コード入力画面へ進む
      setFormData(data);
      setSignupSessionId(responseData.signup_session_id);
      setStep('verification');
    } else {
      const { message, detailMessages } = translateApiError(
        result.error,
        result.details,
      );
      setError({
        message,
        detailMessages,
      });
    }
  };

  const handleVerificationComplete = () => {
    navigate(ROUTE_PATH.SIGN_UP_COMPLETE, {
      state: {
        email: formData!.email,
        firstName: formData!.firstName,
        lastName: formData!.lastName,
        password: formData!.password,
      },
    });
  };

  const handleSignupSessionIdUpdate = (newSessionId: string) => {
    setSignupSessionId(newSessionId);
  };

  return (
    <div className="page p-auth page_a2">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                <div className="l-hero">
                  <img
                    className="l-hero__img"
                    src="/Common_Title.jpg"
                    alt="LOUNGE RESERVATION"
                  />
                </div>
                <div className="l-grid">
                  <section className="l-card">
                    {step === 'input' ? (
                      <SignUpForm
                        onSubmit={handleFormSubmit}
                        isSubmitting={isCreatingAccount}
                        error={error}
                      />
                    ) : (
                      <SignUpVerificationCodeForm
                        email={formData!.email}
                        password={formData!.password}
                        signupSessionId={signupSessionId}
                        firstName={formData!.firstName}
                        lastName={formData!.lastName}
                        onVerified={handleVerificationComplete}
                        onSignupSessionIdUpdate={handleSignupSessionIdUpdate}
                      />
                    )}
                  </section>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
